import { Component, OnChanges, OnDestroy, OnInit, SimpleChanges, computed, effect, input, output, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { Delivery, DeliverableService} from '..';
import { AvailableLanguage, FieldRequirements, Language, LanguageService, reloadOnLocaleChange, TranslatableFormControl, WordAndCharacterRequirements } from '../../language';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslationService } from '../../translation/translation.service';
import { DataProcessingService, FormDataStandard } from '../../utilities';
import { provideTranslocoScope, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { Vote } from '../../votes';
import { Cluster, Culture, CultureScore, CultureService, Difference, DifferenceService, MediaService } from '@frontend/common';
import { CultureClusterWrapper } from '../../clusters/culture-cluster-wrapper.model';
import { CultureScoreBenchmark } from '../../cultures/culture-score-benchmark.model';
import { ClusterService } from '../../clusters/cluster.service';
import { FormControlBooleanRequirements } from '../form-control-requirements.model';
import { InlinePillPickerComponent } from '@frontend/shared';
import { FormControlLickertQuestionComponent } from '../../forms';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { LoaderGlobeAnimationDetailComponent } from '../../loaders';
import { AlertModule } from 'ngx-bootstrap/alert';

interface GenericFormControl {
  key:string;
  defaultRequirements : any;
  deliverableAttribute:string;
  deliverableTitle:boolean;
}
@Component({
  selector: 'multisite-difference-vote-cco',
  templateUrl: './difference-vote-cco.component.html',
  styleUrls: ['./difference-vote-cco.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslocoModule,
    InlinePillPickerComponent,
    FormControlLickertQuestionComponent,
    CollapseModule,
    LoaderGlobeAnimationDetailComponent,
    AlertModule,
  ],
  providers: [
    provideTranslocoScope('social'),
  ],
})
export class DifferenceVoteCcoComponent implements OnInit, OnDestroy, OnChanges {

  // NECESSARY FOR ALL DELIVERABLE COMPONENTS

  delivery = input.required<Delivery>();
  deliveryLocked = input<boolean>(false); // for example, after submission
  formUpdateValueAndValidity = input<number>(0); // update the form value and validity when this changes
  formCachingServiceIdentifier = input.required<string | undefined>(); // required so we can immediately get cached data on instantiation
  disabled = input<boolean>(false);
  parentLoadingObject = input<{[key:string]:boolean}>({});
  loadingObject = signal <{[key:string]:boolean}>({});
  combinedLoadingObject = computed(() => ({...this.parentLoadingObject(),...this.loadingObject()}));
  saveDeliverable = output<{[key:string]:any}>();
  submitDeliverable = output<{formData:{[key:string]:any},allTargetLanguageFormControlsAreNull:boolean}>();
  deliverableForm!: FormGroup;
  deliverable = input<Vote | undefined>(undefined); // Should this be required?
  difference = computed<Difference>(() => this.delivery().differences[0]);
  
  // OPTIONAL FOR DELIVERABLE COMPONENTS (NEEDED BY SOME)
  
  truncatedTitle = input<string | undefined>(); // We will re-use this if the user saves the deliverable with an empty title

  // SPECIFIC TO THIS DELIVERABLE COMPONENT

  allCultures = input.required<Culture[]>();
  culture = input.required<Culture>();

  defaultValueRequirements = {
    required: true,
  };
  defaultPublicCommentRequirements = {
      words: {min: 1, max: 30},
      characters: {min: 2, max: 150},
      required: false,
  };
  defaultPrivateNoteRequirements = {
    words: {min: 5, max: 50},
    characters: {min: 15, max: 120},
    required: false,
  }
  allTranslatableFormControls = signal<TranslatableFormControl[]>([
    {key:'public_comment_translation_source', type:'source', pairKey:'public_comment', defaultRequirements : this.defaultPublicCommentRequirements, deliverableAttribute:'public_comment', deliverableTitle:false},
    {key:'public_comment', type:'target', pairKey:'public_comment', defaultRequirements : this.defaultPublicCommentRequirements, deliverableAttribute:'public_comment', deliverableTitle:false},
  ]);

  subscriptions : Subscription[] = [];

  benchmarkClusters = signal<CultureClusterWrapper[]>([]);
  relatedBenchmarkClusters = computed<CultureClusterWrapper[]>(() => {
    return this.benchmarkClusters().filter(cluster => cluster.cultures.map(c=>c.slug).includes(this.culture().slug) || cluster.slug === 'benchmark-cultures-global');
  });
  relatedBenchmarkClustersWithoutWrappers = computed<Cluster[]>(() => {
    return this.relatedBenchmarkClusters().map(clusterWrapper => clusterWrapper.cluster);
  });
  uniqueCulturesInAllRelatedBenchmarkClusters = computed<Culture[]>(() => {
    return this.clusterService.getUniqueCulturesInCultureClusters(this.relatedBenchmarkClusters()).filter(c => this.culture() ? c.slug != this.culture().slug : true);
  });
  uniqueCcoCulturesInAllRelatedBenchmarkClusters = computed<Culture[]>(() => {
    return this.uniqueCulturesInAllRelatedBenchmarkClusters().filter(c => c.cco);
  });
  uniqueCulturesInSelectedRelatedBenchmarkClusters = computed<Culture[]>(() => {
    const selectedClusters = this.selectedBenchmarkClusters();
    if (!selectedClusters.length) {
      return [];
    }
    const uniqueCultures = selectedClusters.map(cluster => cluster.cultures).reduce((acc, val) => acc.concat(val), [] as Culture[]).filter((culture, index, self) => self.findIndex(c => c.slug === culture.slug) === index);
    return uniqueCultures;
  });
  ccoBenchmarkVotes = signal<Vote[]>([]);

  userSelectedBenchmarkClusters = signal<CultureClusterWrapper[] | undefined>(undefined);
  defaultSelectedBenchmarkClusters = computed<CultureClusterWrapper[]>(() => {
    const relatedClusters = this.relatedBenchmarkClusters();
    const clustersIncludingTheSelectedCulture = relatedClusters.filter(c=>c.slug !== 'benchmark-cultures-global');
    return this.benchmarkClusters().filter(cluster => cluster.slug === 'benchmark-cultures-global').concat(clustersIncludingTheSelectedCulture.length > 2 ? [] : relatedClusters.filter(c=>c.slug !== 'benchmark-cultures-global'));
  });
  selectedBenchmarkClusters = computed<CultureClusterWrapper[]>(() => {
    return this.userSelectedBenchmarkClusters() ? this.userSelectedBenchmarkClusters() as CultureClusterWrapper[] : this.defaultSelectedBenchmarkClusters();
  });
  selectedBenchmarkClusterIds = computed<number[]>(() => {
    return this.selectedBenchmarkClusters().map(b=>b.id);
  });
  culturesGroupedByVoteValue = computed<CultureScoreBenchmark[]>(() => {
    const selectedCultures = this.uniqueCulturesInSelectedRelatedBenchmarkClusters();
    const benchmarkVotes = this.ccoBenchmarkVotes();
    if(selectedCultures.length && benchmarkVotes.length){
      const cultureScores : CultureScore[] = this.cultureService.generateCultureVoteScoreObjects(selectedCultures, benchmarkVotes);
      return this.dataProcessingService.groupObjectsByProperty(cultureScores, 'score',[1,2,3,4,5,6,7] as any);
    } else {
      return [];
    }
  });
  collapseObject = signal<{[key:string]:boolean}>({});
  ccoDifferenceAnchorSet = computed<string[]>(() => {
    if(this.difference()){
      return this.differenceService.generateAnchorSetFromDifference(this.difference(),7);
    }
    return [];
  });
  toggleCollapse(key: string){
    this.collapseObject()[key] = !this.collapseObject()[key];
  }
  setCollapse(key: string, value: boolean){
    this.collapseObject()[key] = value;
  }
  toggleSelectBenchmarkCluster(cluster: any){
    const selectedCultureClusterWrapper = this.benchmarkClusters().find(c=>c.slug === cluster.slug);
    if(!selectedCultureClusterWrapper){
      return;
    }
    const userSelected = this.userSelectedBenchmarkClusters();
    if(userSelected){
      if(userSelected.map(c=>c.slug).includes(cluster.slug)){
        this.userSelectedBenchmarkClusters.set(userSelected.filter(c=>c.slug !== cluster.slug));
      } else {
        this.userSelectedBenchmarkClusters.set(userSelected.concat(selectedCultureClusterWrapper));
      }
    } else {
      const defaults = this.defaultSelectedBenchmarkClusters();
      if(defaults.map(c=>c.slug).includes(cluster.slug)){
        this.userSelectedBenchmarkClusters.set(defaults.filter(c=>c.slug !== cluster.slug));
      } else {
        this.userSelectedBenchmarkClusters.set([...defaults, selectedCultureClusterWrapper]);
      };
    }
  }

  // END OF SPECIFIC TO THIS DELIVERABLE COMPONENT

  // MORE OPTIONAL FOR DELIVERABLE COMPONENTS (NEEDED BY SOME)

  // -- For components with text inputs: --

  deliverable_translations = input<{[key:string]:string} | undefined>(undefined);

  allNonTranslatableFormControls = signal<GenericFormControl[]>([
    {key:'value', defaultRequirements : this.defaultValueRequirements, deliverableAttribute:'value', deliverableTitle:false},
    {key:'private_note', defaultRequirements : this.defaultPrivateNoteRequirements, deliverableAttribute:'private_note', deliverableTitle:false},
  ]);

  allFormControls = computed(() => (this.allNonTranslatableFormControls() as (GenericFormControl | TranslatableFormControl)[]).concat(this.allTranslatableFormControls() as (GenericFormControl | TranslatableFormControl)[]));

  targetControls = computed(() => this.allTranslatableFormControls().filter(control => control.type === 'target'));
  sourceControls = computed(() => this.allTranslatableFormControls().filter(control => control.type === 'source'));

  sourceLanguage = input<Language | undefined>(undefined);
  targetLanguage = input<Language | undefined>(undefined);

  // These signal names are composed in the template by getSignalValue(). You will not find them by string-searches.
  sourceLanguageIsCharacterBased = computed(() => {
    const sourceIso = this.sourceLanguage()?.iso;
    return sourceIso ? this.languageService.characterBasedLanguages?.includes(sourceIso) : false;
  });
  targetLanguageIsCharacterBased = computed(() => {
    const targetIso = this.targetLanguage()?.iso;
    return targetIso ? this.languageService.characterBasedLanguages?.includes(targetIso) : false;
  });

  wordAndCharacterRequirements = signal<{words?:WordAndCharacterRequirements[],characters?:WordAndCharacterRequirements[]}>({});
  taskBasedFormControlRequirements = signal<{required?:FormControlBooleanRequirements}>({});
  translatableFieldRequirements = computed<Record<string, FieldRequirements>>(() => {
    const requirementsObject: Record<string, FieldRequirements> = {};
    this.allTranslatableFormControls().forEach(control => {
      const languageCode = control.type === 'target' ? (this.targetLanguage()?.iso ?? '') : (this.sourceLanguage()?.iso ?? '');
      requirementsObject[control.key] = this.getFieldRequirements(languageCode, control.pairKey, true);
    });
    return requirementsObject;
  });

  editorWords : {[key:string]:number} = {};
  editorCharacters : {[key:string]:number} = {};

  // -- For components which can have local errors: --

  error = signal<string|{message:string,meta:any}|undefined>(undefined);
  errorString = computed(() => {
    if(this.error()){
      return typeof this.error() === 'string' ? this.error() : (this.error() as {message:string,meta:any}).message;
    }
  });
  errorObject = computed(() => {
    if(this.error()){
      return typeof this.error() === 'object' ? this.error() : null;
    }
  });

  // END OF OPTIONAL FOR DELIVERABLE COMPONENTS


  // NOT NECESSARY FOR THIS COMPONENT:

  activeLanguageObject = signal<AvailableLanguage | undefined>(undefined);

  // END OF NOT NECESSARY FOR THIS COMPONENT

  constructor(
    private languageService : LanguageService,
    private translationService : TranslationService,
    private translocoService : TranslocoService,
    private cultureService : CultureService,
    private dataProcessingService : DataProcessingService,
    private clusterService : ClusterService,
    private differenceService : DifferenceService,
    private mediaService : MediaService,
    private deliverableService : DeliverableService,
  ) {

    // SPECIFIC TO THIS DELIVERABLE COMPONENT
    // Reload cultures + benchmark clusters in the new language. Both CultureService
    // and ClusterService cache per locale (and cluster relationships come from
    // CacheStorage), so this asks for the cache, not `fresh`: a language already
    // visited is served instantly with no request.
    reloadOnLocaleChange(() =>
      this.getCultureClustersData('geographic', 'national', 'benchmark_cultures', 'frequent', false)
    );
    effect(() => {
      const ccoCultures = this.uniqueCcoCulturesInAllRelatedBenchmarkClusters();
      if (
        this.difference() &&
        ccoCultures.length > 0) {
        const cultureIds = ccoCultures.map(culture => culture.id);
        this.differenceService.getDifferenceVotes(this.difference(),cultureIds,'benchmark','cco_import')
          .subscribe(data => {
            this.ccoBenchmarkVotes.set(data);
          });
      }
    },{
      allowSignalWrites: true,
    });
    // END OF SPECIFIC TO THIS DELIVERABLE COMPONENT
  }

  isAnythingLoading(){
    for (let something in this.combinedLoadingObject()){
      if (this.combinedLoadingObject()[something]){
        return true;
      }
    }
    return false;
  }
  setLoadingStatus(key: string, value: boolean){
    // So we can detect deep changes in the object
    this.loadingObject.set({...this.loadingObject(),[key]:value});
  }

  getTranslatableFormFieldControls(pairKey : string){
    return this.allTranslatableFormControls() ? this.allTranslatableFormControls().filter(control => control.pairKey === pairKey) : [];
  };
  getFormFieldControls(key : string){
    return this.allNonTranslatableFormControls() ? this.allNonTranslatableFormControls().filter(control => control.key === key) : [];
  };

  getFieldRequirements(language_code : string | undefined, field_key : string, translatable : boolean) : FieldRequirements{

    return this.deliverableService.getFieldRequirements(
      this.allTranslatableFormControls(),
      this.allNonTranslatableFormControls(),
      this.wordAndCharacterRequirements(),
      this.taskBasedFormControlRequirements(),
      language_code ?? '',
      field_key,
      translatable
    );
  };
  
  getSignalValue(signalName: string) {
    return (this as any)[signalName]?.();
  }
  getErrorTranslationKey(): string | undefined {
    const localError = this.error();
    if (localError && typeof localError === 'object') {
      return localError.message;
    }
    return typeof localError === 'string' ? localError : undefined;
  }
  getTranslatableControlValidationClasses(controlKey: string): string {
    const control = this.deliverableForm?.get(controlKey);
    const isInvalid = !!(control?.invalid && (control?.touched || control?.dirty));
    const isValid = !!(control?.valid && control?.touched && control?.dirty);
    return `input-group textarea-control mb-3 ${isInvalid ? 'ng-invalid' : ''} ${isValid ? 'ng-valid' : ''}`.trim();
  }
  getPrivateNoteValidationClasses(): string {
    return this.getTranslatableControlValidationClasses('private_note');
  }
  getCharacterProgressClass(controlKey: string): string {
    const requirements = this.translatableFieldRequirements()?.[controlKey];
    const currentValue = this.formControlCharacterCount(this.deliverableForm, controlKey);
    const maxWarning = requirements?.characters_max_warning ?? Number.POSITIVE_INFINITY;
    const minWarning = requirements?.characters_min_warning ?? Number.NEGATIVE_INFINITY;
    const isOutsideWarningBounds =
      currentValue > maxWarning ||
      currentValue < minWarning;
    return `progress-indicator ${isOutsideWarningBounds ? 'traffic-light-1' : 'traffic-light-6'}`;
  }
  getMax100 (progress : number){
    return Math.min(progress,100);
  }
  formControlCharacterCount(form : FormGroup, controlName: string): number {
    const control = form.get(controlName);
    return control?.value ? control.value.length : 0;
  }

    // FOR ANY COMPONENT WITH TRANSLATABLE TEXT INPUTS

    sendForTranslation(){
      this.error.set(undefined);
      const sourceIso = this.sourceLanguage()?.iso;
      const targetIso = this.targetLanguage()?.iso;
      if(!sourceIso || !targetIso){
        return;
      }
      const textsAsObject = this.deliverableService.getFormSourceLanguageControlsAsTextObjectForTranslation(this.sourceControls(),this.deliverableForm);
      const defaultLocale : string = this.translationService.getDefaultLocaleFromGenericLanguage(targetIso);
      if(this.allTargetLanguageFormControlsAreNull()){
        this.getTranslation(textsAsObject,defaultLocale,sourceIso);
      } else {
        const translation = this.translocoService.translate('contributions.confirm.translation_overwrite');
        if (confirm(translation)) {
          this.getTranslation(textsAsObject,defaultLocale,sourceIso);
        }
      }
    }
    getTranslation(textsAsObject:{[key:string]:string},target_lang : string, source_lang : string) {
      const keys = Object.keys(textsAsObject);
      const values = Object.values(textsAsObject);
      this.error.set(undefined);
      this.setLoadingStatus('translate',true);
      const translationSubscription = this.translationService.getTranslation(textsAsObject,target_lang,source_lang).subscribe((translations : {[key:string]:string}) => {
        this.setLoadingStatus('translate',false);
        this.deliverableService.handleFormTranslations(translations,this.deliverableForm, this.allTranslatableFormControls(), this.deliveryLocked());
      }, error => {
        this.setLoadingStatus('translate',false);
        this.error.set(error);
      });
      this.subscriptions.push(translationSubscription);
    }

    // -- For components with non-required translatable text fields

    optionalTranslatableControlHasMissingTranslation(controlDefinition: TranslatableFormControl): boolean | null { // This function is only needed for components with a translatable field which is also not a required field
      // Let's warn the user when a non-required field is written but the translation is missing
      // The source language is in the form but the target language is empty
      // If the delivery expects a target language, we will not save the untranslated source (target language is empty) when the delivery is submitted.
      // If the delivery does not expect a specify a language, we will save the untranslated source (target language is empty) when the delivery is submitted.
      return this.deliverableService.optionalTranslatableControlHasMissingTranslation(
        this.deliverableForm,
        this.allTranslatableFormControls(),
        controlDefinition
      );
    }  


  // END OF FOR ANY COMPONENT WITH TRANSLATABLE TEXT INPUTS


  getSavedAndCachedFormData(): FormDataStandard {
    const sourceLanguage = this.sourceLanguage() as Language;
    return this.deliverableService.getSavedAndCachedFormData(
      this.formCachingServiceIdentifier() ?? '',
      this.allNonTranslatableFormControls(),
      this.allTranslatableFormControls(),
      this.deliverable() as Vote,
      sourceLanguage,
      this.deliverable_translations() as {[key:string]:string}
    );
  }

  cacheFormData(): void {
    const sourceLanguage = this.sourceLanguage() as Language;
    this.deliverableService.cacheFormData(
      this.formCachingServiceIdentifier() ?? '',
      this.deliverableForm,
      this.allNonTranslatableFormControls(),
      this.allTranslatableFormControls(),
      sourceLanguage
    );
  }
  
  checkAllCachedTargetLanguageFieldsAreNull(cachedFormData : FormDataStandard) : boolean {
    return this.targetControls().every(control => !cachedFormData?.[control.key]);
  };
  allTargetLanguageFormControlsAreNull() {
    if(!this.deliverableForm){return true;} 
    return this.targetControls().every(control => !this.deliverableForm.get(control.key)?.value);
  };

  // deliverableTargetLanguageTitleFormControlsIsNull is for debugging; you can comment it out

  // deliverableTargetLanguageTitleFormControlsIsNull() : boolean {
  //   if(!this.deliverableForm){return true;}
  //   const control = this.targetControls().find(control => control.deliverableTitle);
  //   let value = control ? this.deliverableForm.get(control.key)?.value : null;
  // value = typeof value === 'string' ? value.trim() : value;
  //   return !(value ?? false);
  // };

  // end of debugging

  noSourceLanguageFormControlsAreNull() : boolean {
    return this.deliverableService.noFormControlsAreNull(this.deliverableForm,this.sourceControls());
  };
  disableAllTargetLanguageTranslatableFields(form : FormGroup){
    this.deliverableService.disableFormControls(this.deliverableForm,this.targetControls());
  };
  addOrRemoveEditingLanguage(language: Language | undefined): void {
    this.deliverableService.addOrRemoveEditingLanguage(
      this.deliverableForm,
      this.formCachingServiceIdentifier() ?? '',
      this.allTranslatableFormControls(),
      this.allNonTranslatableFormControls(),
      this.targetControls(),
      language as Language,
      this.deliverable() as Vote,
      this.deliverable_translations() as {[key:string]:string},
      this.wordAndCharacterRequirements(),
      this.taskBasedFormControlRequirements(),
      this.deliveryLocked(),
      this.targetLanguage(),
    );
  }

  disableTargetLanguageTranslatableFieldsIfNecessary(sourceLanguage : Language | undefined){ // Applies the rule that the source language should be done first // TODO - move this function from all components to the DeliverableService (it's mostly duplicated there)
    const cachedFormData = this.getSavedAndCachedFormData();
    if(sourceLanguage && this.checkAllCachedTargetLanguageFieldsAreNull(cachedFormData)){
      this.disableAllTargetLanguageTranslatableFields(this.deliverableForm);
    }
  };
  showCharacters(characterCount : number, editorKey : string){
    this.editorCharacters[editorKey] = characterCount;
  }
  showWords(wordCount : number, editorKey : string){
    this.editorWords[editorKey] = wordCount;
  }

  save(action : 'update' | 'submit'){
    const formData = this.deliverableService.prepareFormDataForSaving(
      this.deliverableForm,
      this.allTranslatableFormControls(),
      this.allNonTranslatableFormControls(),
      this.sourceLanguage() as Language,
      this.truncatedTitle(),
      action,
      this.delivery().target_lang
        );
    if(action === 'update'){
      this.saveDeliverable.emit(formData);
    } else if (action === 'submit'){
      this.submitDeliverable.emit({formData:formData,allTargetLanguageFormControlsAreNull:this.allTargetLanguageFormControlsAreNull()});
    }
  }

  initialiseDeliverableForm(){

    const cachedFormData : FormDataStandard = this.getSavedAndCachedFormData();
    const translationTargetFormControls = this.allTranslatableFormControls().filter(control => control.type === 'target');

    const formGroupObject = this.deliverableService.generateInitialFormGroupObject(
      cachedFormData,
      this.allNonTranslatableFormControls(),
      translationTargetFormControls,
      this.targetLanguage() as Language,
      this.wordAndCharacterRequirements(),
      this.taskBasedFormControlRequirements(),
      this.deliverable(),
    );
    this.deliverableForm = new FormGroup(formGroupObject);
    const formSubscription = this.deliverableForm.valueChanges.subscribe(() => {
      this.cacheFormData();
    });
    for (const control of translationTargetFormControls) {
      if(cachedFormData?.[control.key]){
        this.deliverableForm.get(control.key)?.markAsDirty();
      }
    }
    if(this.deliveryLocked()){
      this.deliverableForm.disable();
    }
    this.subscriptions.push(formSubscription);
  }


  // SPECIFIC TO THIS DELIVERABLE COMPONENT

  getFlagUrlFromHash(hash:string,transformations : string = ''){
    return this.mediaService.getFlagUrlFromHash(hash,transformations,true,'.png');
  }
  formIsValid(){
    // The form is valid when this.deliverableForm.get('value') is null, so we need to check that it is not null. This is because range inputs cannot be null.
    return !!(this.deliverableForm && this.deliverableForm.valid && this.deliverableForm.get('value')?.value); // 
  };

  getCultureClustersData (culture_category: string, culture_type: string, cluster_category: string, cluster_type:string, freshFromServer:boolean){
    this.cultureService.getCultureClustersData(culture_category,culture_type,cluster_category,cluster_type,freshFromServer).subscribe(([cultures, clusters, cultureClusters]) => {
      let results = [cultures, clusters, cultureClusters];
      if(this.allCultures().length > 0){
        this.benchmarkClusters.set(this.cultureService.prepareCultureClusters(cultures,clusters,cultureClusters,'benchmark_cultures','frequent'));
      }
    }, error => {
      // TODO Handle errors
    });
  }

  // END OF SPECIFIC TO THIS DELIVERABLE COMPONENT


  initialise(freshFromServer : boolean = false){

    // -- For components with translatable text inputs --

    this.wordAndCharacterRequirements.set(this.delivery().task?.meta);
    this.taskBasedFormControlRequirements.set(this.delivery().task?.meta);

    // -- For all deliverable components --

    this.initialiseDeliverableForm();

    // -- For components with translatable text inputs --

    this.addOrRemoveEditingLanguage(this.sourceLanguage());

    // -- For this specific deliverable component --

    this.getCultureClustersData('geographic', 'national', 'benchmark_cultures', 'frequent', freshFromServer);

    if(this.deliverableForm?.get('public_comment')?.value){
      this.setCollapse('public_comment',false);
    } else {
      this.setCollapse('public_comment',true);
    }
    if(this.deliverableForm?.get('private_note')?.value){
      this.setCollapse('private_note',false);
    } else {
      this.setCollapse('private_note',true);
    }


  }
  ngOnInit(): void {

    // SPECIFIC TO THIS DELIVERABLE COMPONENT

    this.collapseObject().benchmarks = true;

    // END OF SPECIFIC TO THIS DELIVERABLE COMPONENT

    this.initialise(false);

  }
  ngOnDestroy () : void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Apply the form's enabled state from its two independent sources.
   *
   * `deliveryLocked` is **permanent** — the delivery has been submitted and
   * must never be editable again. `disabled` is **transient** — the parent
   * binds it to `isAnythingLoading()`, so it toggles around every load and
   * save. Either disables the form; only when both are clear may it be enabled.
   *
   * This previously read the transient flag alone and called `enable()`
   * unconditionally when it cleared, re-opening a submitted delivery for
   * editing as soon as its first load finished. Kept in step with the identical
   * method in InsightEditComponent.
   */
  private applyFormEnabledState(): void {
    if(!this.deliverableForm){
      return;
    }

    if(this.disabled() || this.deliveryLocked()){
      this.deliverableForm.disable();

      return;
    }

    this.deliverableForm.enable();

    if(this.sourceLanguage()){
      this.disableTargetLanguageTranslatableFieldsIfNecessary(this.sourceLanguage());
    }
  }

  ngOnChanges (changes: SimpleChanges) {

    if(changes.formUpdateValueAndValidity && this.deliverableForm){
      this.deliverableForm.updateValueAndValidity();
    }
    if((changes.disabled || changes.deliveryLocked) && this.deliverableForm){
      this.applyFormEnabledState();
    }
    if(changes.sourceLanguage && this.deliverableForm){
      if(changes.sourceLanguage){
        this.addOrRemoveEditingLanguage(changes.sourceLanguage.currentValue);
      }
    }
    
  }

}
